import React, { useState, useMemo, useEffect, useRef } from 'react';
import { CSVLink } from 'react-csv';
import useApiContext from 'hooks/useApiContext';
import useModalContext from 'hooks/useModalContext';
import {
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  TextField,
  InputAdornment,
  Box,
  Typography,
  Drawer,
  IconButton,
  Chip,
} from '@mui/material';
import {
  useLocation,
  Outlet,
  Link,
  useParams,
  useNavigate,
} from 'react-router-dom';
import { format, isAfter } from 'date-fns';
import useRepo from 'hooks/useRepo';
import { T, O, M, A } from 'TOMA';
import s from './ExhibitorList.module.scss';
import routes from 'router/routes';
import {
  PaginationState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { Switch } from 'components/Switch';
import { CustomLink } from 'components/CustomLink';
import { InputStand } from 'components/InputStand';
import { ExhibitorListHeader } from 'components/ExhibitorListHeader';
import Extraction from 'components/Extraction/Extraction';
import { eRoles } from '../../interfaces/Roles';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import IosShareIcon from '@mui/icons-material/IosShare';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ReactComponent as PdfSvg } from '../../assets/svg/pdf.svg';

const columnHelper = createColumnHelper<any>();

const ORDER_STATUS_LABELS: Record<string, string> = {
  offre_envoyee: 'Offre envoyée',
  offre_validee: 'Offre validée',
  offre_refusee: 'Offre refusée',
};
const ORDER_STATUS_COLORS: Record<string, string> = {
  offre_envoyee: '#ea7611',
  offre_validee: '#16a34a',
  offre_refusee: '#dc2626',
};

const OFFER_STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'envoyee', label: 'Envoyée' },
  { value: 'refusee', label: 'Refusée' },
  { value: 'validee', label: 'Validée' },
  { value: 'a_contacter', label: 'A contacter' },
  { value: 'en_attente', label: 'En attente' },
];

function OfferStatusDropdown({
  value,
  onSave,
}: {
  value: string;
  onSave: (newStatus: string) => Promise<void>;
}): JSX.Element {
  const [current, setCurrent] = useState(value);
  const { setModalContent, setModalVisible } = useModalContext();

  useEffect(() => {
    setCurrent(value);
  }, [value]);

  const handleChange = (event: { target: { value: unknown } }): void => {
    const newStatus = event.target.value as string;
    const previous = current;

    setModalContent(
      <div style={{ width: 420, maxWidth: '90vw' }}>
        <A.Text fontSize="18" fw="bold" mb="20" textAlign="center">
          Confirmer le changement de statut de l&apos;offre ?
        </A.Text>
        <T.Flex gap={2} valign="center" mb="-15">
          <A.Button
            bg="alert"
            onClick={async () => {
              setModalVisible(false);
              setCurrent(newStatus);
              try {
                await onSave(newStatus);
              } catch (error: any) {
                toast.error(error.message);
                setCurrent(previous);
              }
            }}
          >
            Confirmer
          </A.Button>
          <A.Button
            appearance="link"
            color="grey-80"
            textDecoration="underline"
            onClick={() => {
              setModalVisible(false);
            }}
          >
            Annuler
          </A.Button>
        </T.Flex>
      </div>
    );
    setModalVisible(true);
  };

  return (
    <FormControl size="small" sx={{ minWidth: 150 }}>
      <Select
        value={current ?? ''}
        onChange={handleChange}
        sx={{ fontSize: 12, bgcolor: '#fff' }}
      >
        {OFFER_STATUS_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 12 }}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function SignatureStatusLabel({ order }: { order: any }): JSX.Element {
  const isSigned =
    order.signature_status === 'signed' ||
    order.signature_status === 'completed';
  const isDeclined = order.signature_status === 'declined';
  const isExpired = order.signature_status === 'expired';
  const color = isSigned ? '#16a34a' : isDeclined ? '#dc2626' : '#ea7611';
  const text = isSigned
    ? 'Signé'
    : isDeclined
    ? 'Refusé'
    : isExpired
    ? 'Expiré'
    : 'En attente';

  return (
    <span style={{ color, fontWeight: 600 }}>
      Signature: {text}
      {order.signed_at &&
        ` (${format(new Date(order.signed_at), 'dd/MM/yyyy HH:mm')})`}
    </span>
  );
}

function OrderDetailModal({
  order,
  onStatusChange,
  onHide,
  onClose,
}: {
  order: any;
  onStatusChange: (status: string) => Promise<void>;
  onHide: () => Promise<void>;
  onClose: () => void;
}): JSX.Element {
  const items = Array.isArray(order.items) ? order.items : [];
  const label = ORDER_STATUS_LABELS[order.status] ?? order.status;
  const color = ORDER_STATUS_COLORS[order.status] ?? '#6b7280';
  const fmt = (n: number): string =>
    new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(n);

  return (
    <div style={{ width: 560, maxWidth: '90vw' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 4,
        }}
      >
        <h3 style={{ margin: 0 }}>Commande #{order.id}</h3>
        <span style={{ color, fontWeight: 600, fontSize: 14 }}>{label}</span>
      </div>
      <div
        style={{
          color: '#6b7280',
          fontSize: 12,
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span>
          {order.created_at
            ? format(new Date(order.created_at), 'dd/MM/yyyy HH:mm')
            : ''}
        </span>
        {order.signature_status && <SignatureStatusLabel order={order} />}
      </div>

      <div
        style={{
          background: '#f9fafb',
          borderRadius: 8,
          padding: 14,
          marginBottom: 16,
          fontSize: 13,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Société</div>
        <div>{order.company_name ?? '—'}</div>
        {order.siret && (
          <div style={{ color: '#6b7280', fontFamily: 'monospace' }}>
            SIRET: {order.siret}
          </div>
        )}
        {order.address && (
          <div style={{ color: '#6b7280' }}>
            {order.address}
            {order.postal_code ? `, ${order.postal_code}` : ''}{' '}
            {order.city ?? ''}
          </div>
        )}
      </div>

      <div
        style={{
          background: '#f9fafb',
          borderRadius: 8,
          padding: 14,
          marginBottom: 16,
          fontSize: 13,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Contact</div>
        <div>
          {order.contact_first_name} {order.contact_last_name}
        </div>
        {order.contact_email && (
          <div style={{ color: '#6b7280' }}>{order.contact_email}</div>
        )}
        {order.contact_phone && (
          <div style={{ color: '#6b7280' }}>{order.contact_phone}</div>
        )}
        {order.contact_function && (
          <div style={{ color: '#6b7280' }}>{order.contact_function}</div>
        )}
      </div>

      {order.form_json && Object.keys(order.form_json).length > 0 && (
        <div
          style={{
            background: '#f9fafb',
            borderRadius: 8,
            padding: 14,
            marginBottom: 16,
            fontSize: 13,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            Réponses au formulaire
          </div>
          {Object.entries(order.form_json).map(([key, value]) => (
            <div key={key} style={{ marginBottom: 6 }}>
              <div style={{ color: '#6b7280', fontSize: 12 }}>
                {key.replace(/_/g, ' ')}
              </div>
              <div>
                {Array.isArray(value) ? value.join(', ') : String(value ?? '—')}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
          Offres sélectionnées
        </div>
        <table
          style={{
            width: '100%',
            fontSize: 13,
            borderCollapse: 'collapse',
          }}
        >
          <thead>
            <tr
              style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}
            >
              <th style={{ padding: '6px 0' }}>Offre</th>
              <th style={{ padding: '6px 0', textAlign: 'right' }}>Qté</th>
              <th style={{ padding: '6px 0', textAlign: 'right' }}>
                Prix unit.
              </th>
              <th style={{ padding: '6px 0', textAlign: 'right' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it: any, idx: number) => {
              const qty = it.quantity ?? 1;
              return (
                <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '6px 0' }}>{it.title}</td>
                  <td style={{ padding: '6px 0', textAlign: 'right' }}>
                    {qty}
                  </td>
                  <td style={{ padding: '6px 0', textAlign: 'right' }}>
                    {fmt(it.price ?? 0)}
                  </td>
                  <td style={{ padding: '6px 0', textAlign: 'right' }}>
                    {fmt((it.price ?? 0) * qty)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid #e5e7eb' }}>
              <td
                colSpan={3}
                style={{
                  padding: '8px 0',
                  fontWeight: 700,
                  textAlign: 'right',
                }}
              >
                Total HT
              </td>
              <td
                style={{
                  padding: '8px 0',
                  fontWeight: 700,
                  textAlign: 'right',
                }}
              >
                {fmt(order.total_ht ?? 0)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 10,
          justifyContent: 'space-between',
          paddingTop: 12,
          borderTop: '1px solid #e5e7eb',
        }}
      >
        <button
          type="button"
          title="Masquer cette commande"
          onClick={onHide}
          style={{
            padding: '8px 14px',
            background: '#fff',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 13,
            color: '#b91c1c',
          }}
        >
          Supprimer
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 20px',
              background: '#fff',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            Fermer
          </button>
          {order.status !== 'offre_refusee' && (
            <button
              type="button"
              onClick={async () => await onStatusChange('offre_refusee')}
              style={{
                padding: '8px 20px',
                background: '#fee2e2',
                color: '#dc2626',
                border: '1px solid #fca5a5',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              Refuser
            </button>
          )}
          {order.status !== 'offre_validee' && (
            <button
              type="button"
              onClick={async () => await onStatusChange('offre_validee')}
              style={{
                padding: '8px 20px',
                background: '#16a34a',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              Valider
            </button>
          )}
          {order.status !== 'offre_envoyee' && (
            <button
              type="button"
              onClick={async () => await onStatusChange('offre_envoyee')}
              style={{
                padding: '8px 20px',
                background: '#fef3cd',
                color: '#ea7611',
                border: '1px solid #fcd34d',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              En attente
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ExhibitorList(): JSX.Element {
  const [csvError, setCsvError] = useState<string | undefined>(undefined);
  const [csvSuccess, setCsvSuccess] = useState<boolean>(false);
  const [csvLoading, setCsvLoading] = useState<boolean>(false);
  const { setModalContent, setModalVisible } = useModalContext();
  const { pathname } = useLocation();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const {
    userRepository,
    exhibitorRepository,
    exhibitorEventRepository,
    codificationRepository,
    preferenceRepository,
    eventRepository,
  } = useApiContext();
  const user = userRepository.getUser();
  const isModerator = user?.role === eRoles.MODERATOR;
  const isAdmin = user?.role === eRoles.ADMIN;
  const [filterCollectif, setFilterCollectif] = useState<any>();
  const [filterName, setFilterName] = useState<any>();
  const [reload, setReload] = useState<number>(0);
  const [orders, setOrders] = useState<any[]>([]);
  const { eventId } = useParams();
  const { t } = useTranslation();
  const {
    response = [],
    loading,
    error,
  } = useRepo(async () => {
    if (!pathname.includes('events')) {
      return await exhibitorRepository.getExhibitorsWithoutEvent();
    } else {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return await exhibitorRepository.getExhibitors(eventId!);
    }
  }, [reload]);
  const [isDataReady, setIsDataReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const downloadCSV = async () => {
    try {
      toast('Start file generation', { type: 'info' });
      setIsLoading(true);
      const response = await exhibitorRepository.downloadCSV(eventId);
      setTimeout(() => {
        toast('Now you can download the file', { type: 'success' });
      }, 500);
      setCsvData(response.data.data);
      setIsDataReady(true);
      setIsLoading(false);
    } catch (error) {
      console.error('Error downloading the file:', error);
      setIsLoading(false);
    }
  };

  const downloadPdf = async (exhibId: string): Promise<void> => {
    try {
      // toast('Start file generation', { type: 'info' });
      // setIsLoading(true);
      const response = await exhibitorRepository.downloadPdf(eventId, exhibId);
      // const blob = new Blob([response.data], { type: 'application/zip' });
      // const url = window.URL.createObjectURL(blob);
      // const a = document.createElement('a');
      // a.href = url;
      // a.download = 'product_pdfs.zip';
      // a.click();
      // window.URL.revokeObjectURL(url);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');

      const contentType = response.headers['content-type'];

      let fileName = `product_${new Date().getTime()}`;

      if (contentType.includes('pdf')) {
        fileName += '.pdf';
      } else {
        fileName += '.zip';
      }

      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(error.message);
      console.error('Error downloading the file:', error);
      setIsLoading(false);
    }
  };

  const { response: dateLimit } = useRepo(
    async () => await preferenceRepository.find('date_limit'),
    [reload]
  );

  const { response: eventData } = useRepo(
    async () => (eventId ? await eventRepository.getEventById(eventId) : null),
    [eventId]
  );
  const eventName: string = (eventData as any)?.name ?? '';
  const eventYear: string | number = (eventData as any)?.year ?? '';

  // Load orders for the event
  useEffect(() => {
    if (!eventId || !pathname.includes('events')) return;
    eventRepository
      .listOrders(eventId)
      .then((data) => {
        setOrders(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error('Failed to load orders:', err);
        setOrders([]);
      });
  }, [eventId, pathname, eventRepository, reload]);

  const { response: codifCollectif } = useRepo(
    async () => await codificationRepository.get('collectif')
  );
  const columns = useMemo(() => {
    const baseColumns = [
      columnHelper.accessor('status', {
        header: t('event:status'),
        cell: (info) => {
          const val = info.getValue();
          return (
            <div className={s.ExhibitorList__status} data-key={val}>
              {val}
            </div>
          );
        },
      }),
      columnHelper.accessor('number', {
        header: t('admin:number'),
      }),
      // columnHelper.accessor('active', {
      //   header: 'Activé',
      //   enableSorting: false,
      //   cell: ({ getValue }) => {
      //     const { value, userId, exhibId } = getValue();
      //     return (
      //       <Switch
      //         key={exhibId}
      //         defaultChecked={value}
      //         onChange={async (e) =>
      //           await userRepository.updateUserActive(e, userId)
      //         }
      //       />
      //     );
      //   },
      // }),
      columnHelper.accessor('locked', {
        header: t('admin:locked'),
        enableSorting: false,
        cell: ({ getValue }) => {
          const { value, exhibId } = getValue();
          return (
            <Switch
              key={exhibId}
              checked={value}
              onChange={async (status) =>
                await exhibitorRepository
                  .mergeUserExhibitor('locked', status, exhibId, eventId)
                  .finally(() => setReload((prev) => prev + 1))
              }
            />
          );
        },
      }),
      columnHelper.accessor('raisonSocial', {
        header: t('admin:company'),
        cell: (info) => {
          const val = info.getValue();
          return (
            <CustomLink to={`${pathname}/exhibitor/${val.exhibId}`}>
              <b>{val.value}</b>
            </CustomLink>
          );
        },
      }),
      columnHelper.accessor('nomCommercial', {
        header: t('exhibitors:nom_commercial'),
      }),
      columnHelper.accessor('collectif', {
        header: t('exhibitors:participation'),
        cell: (info) => (
          <div className={s.ExhibitorList__collectif}>
            {info.renderValue().length > 0 ? info.renderValue() : 'Individual'}
          </div>
        ),
      }),
      columnHelper.accessor('numeroHall', {
        header: t('exhibitors:numero_de_stand'),
        enableSorting: false,
        cell: (info) => {
          const { stand, hall, exhibId } = info.renderValue();
          return (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <T.Flex key={exhibId} valign="center" gap="1">
                <InputStand
                  label="Stand"
                  defaultValue={stand}
                  disable={user?.role === 'MODERATOR'}
                  onChange={async (e) =>
                    await exhibitorRepository
                      .mergeUserExhibitor('stand', e, exhibId, eventId)
                      .finally(() => setReload((r) => r + 1))
                  }
                />
                <InputStand
                  label="Hall"
                  defaultValue={hall}
                  disable={user?.role === 'MODERATOR'}
                  onChange={async (e) =>
                    await exhibitorRepository
                      .mergeUserExhibitor('hall', e, exhibId, eventId)
                      .finally(() => setReload((r) => r + 1))
                  }
                />
              </T.Flex>
            </div>
          );
        },
      }),
      columnHelper.accessor('offre', {
        header: 'Offre',
        enableSorting: false,
        cell: ({ getValue }) => {
          const {
            orders: exhibOrders,
            offerStatus,
            exhibId,
          } = getValue() as {
            orders: any[];
            offerStatus: string;
            exhibId: number;
          };
          if (exhibOrders.length === 0) {
            return (
              <OfferStatusDropdown
                value={offerStatus}
                onSave={async (newStatus: string) => {
                  await exhibitorRepository.mergeUserExhibitor(
                    'offer_status',
                    newStatus,
                    exhibId,
                    eventId
                  );
                  setReload((r) => r + 1);
                }}
              />
            );
          }
          const statusColors: Record<string, string> = {
            offre_envoyee: '#ea7611',
            offre_validee: '#16a34a',
            offre_refusee: '#dc2626',
          };
          const statusLabels: Record<string, string> = {
            offre_envoyee: 'Envoyée',
            offre_validee: 'Validée',
            offre_refusee: 'Refusée',
          };
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {exhibOrders.map((order: any) => {
                const label = statusLabels[order.status] ?? order.status;
                const color = statusColors[order.status] ?? '#6b7280';
                const bgColors: Record<string, string> = {
                  offre_envoyee: '#fef3cd',
                  offre_validee: '#dcfce7',
                  offre_refusee: '#fee2e2',
                };
                const bg = bgColors[order.status] ?? '#f3f4f6';
                return (
                  <button
                    key={order.id}
                    type="button"
                    onClick={() => {
                      setModalContent(
                        <OrderDetailModal
                          order={order}
                          onStatusChange={async (s: string) => {
                            await eventRepository.updateOrderStatus(
                              order.id,
                              s
                            );
                            setModalVisible(false);
                            setReload((r) => r + 1);
                          }}
                          onHide={async () => {
                            await eventRepository.hideOrder(order.id);
                            setModalVisible(false);
                            setReload((r) => r + 1);
                          }}
                          onClose={() => setModalVisible(false)}
                        />
                      );
                      setModalVisible(true);
                    }}
                    style={{
                      background: bg,
                      color,
                      border: `1px solid ${color}`,
                      borderRadius: 6,
                      padding: '3px 10px',
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          );
        },
      }),
      columnHelper.accessor('edit', {
        header: '',
        cell: (info) => {
          const val = info.getValue();
          return pathname === '/vins-de-loire' ? (
            <CustomLink to={`${pathname}/exhibitor/${val.exhibitorId}`}>
              {t('exhibitors:edit')}
            </CustomLink>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                color="error"
                size="small"
                key={val.exhibId}
                type="button"
                sx={{
                  fontSize: 12,
                  borderRadius: '6px',
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                }}
                onClick={async () =>
                  await exhibitorRepository
                    .removeInvite(val.exhibitorId, eventId)
                    .then(() => {
                      setModalVisible(false);
                      setReload((prev) => prev + 1);
                    })
                }
              >
                Retirer
              </Button>
            </div>
          );
        },
      }),
    ];

    // Add the "showroom" column only if the user's role is not "MODERATOR"
    if (user?.role !== 'MODERATOR') {
      baseColumns.splice(
        3,
        0,
        columnHelper.accessor('visibility', {
          header: t('event:participer'),
          enableSorting: false,
          cell: ({ getValue }) => {
            const { value, exhibId } = getValue();
            return (
              <Switch
                key={exhibId}
                checked={value}
                onChange={async (e) =>
                  await exhibitorRepository.mergeUserExhibitor(
                    'visibility',
                    e,
                    exhibId,
                    eventId
                  )
                }
              />
            );
          },
        })
      );
      baseColumns.splice(
        3,
        0,
        columnHelper.accessor('showroom', {
          header: 'Hub',
          enableSorting: false,
          cell: ({ getValue }) => {
            const { value, exhibId, exhibEventId } = getValue();
            return (
              <Switch
                key={exhibId}
                checked={value}
                onChange={async (e: boolean) => {
                  if (!e) {
                    setModalContent(
                      <div
                        style={{
                          width: '600px',
                          maxWidth: '90vw',
                        }}
                      >
                        <A.Text
                          fontSize="22"
                          fw="bold"
                          mb="20"
                          textAlign="center"
                        >
                          {t('users:confirm_remove_product')}
                        </A.Text>

                        <T.Flex gap={2} valign="center" mb="-15">
                          <A.Button
                            bg="alert"
                            onClick={async () => {
                              // Confirm → actually turn OFF
                              const data = { showroom: false };

                              await exhibitorEventRepository.mergeExhibitorEvent(
                                parseInt(exhibEventId),
                                data
                              );

                              setReload((prev) => prev + 1);
                              setModalVisible(false);
                            }}
                          >
                            {t('users:confirm')}
                          </A.Button>

                          <A.Button
                            appearance="link"
                            color="grey-80"
                            textDecoration="underline"
                            onClick={() => {
                              setModalVisible(false);
                            }}
                          >
                            {t('users:cancel')}
                          </A.Button>
                        </T.Flex>
                      </div>
                    );

                    setModalVisible(true);

                    throw new Error('User cancelled');
                  }

                  const data = { showroom: true };

                  await exhibitorEventRepository.mergeExhibitorEvent(
                    parseInt(exhibEventId),
                    data
                  );

                  setReload((prev) => prev + 1);
                  return true;
                }}
              />
            );
          },
        })
      );

      baseColumns.splice(
        4,
        0,
        columnHelper.accessor('showroom', {
          header: 'PDF',
          enableSorting: false,
          cell: ({ getValue }) => {
            const { value, exhibId, exhibEventId } = getValue();
            return (
              <Button
                color="error"
                key={value.exhibId}
                type="button"
                className={s.Exhibitor__downloadPDF}
                onClick={async () => {
                  await downloadPdf(exhibId);
                }}
              >
                <PdfSvg />
              </Button>
            );
          },
        })
      );
    }

    return baseColumns;
  }, []);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [selectedAction, setSelectedAction] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleActionChange = async (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    const action = event.target.value as string;
    setSelectedAction(action);

    switch (action) {
      case 'addExhibitors':
        // navigate to add exhibitors page or handle the action
        break;
      case 'exportSimple':
        // Handle the export CSV logic
        break;
      case 'uploadCSV':
        // Trigger CSV upload functionality
        break;
      case 'extractComplete':
        // Handle the complete extraction logic
        break;
      case 'exportExhibitors':
        // Handle exporting exhibitors
        break;
      default:
        break;
    }
  };
  type StatusKey = 'TRANSMIS' | 'ENREGISTRE' | 'EN_COURSE';

  const status: Record<StatusKey, string> = {
    TRANSMIS: 'Transmis',
    ENREGISTRE: 'Enregistré',
    EN_COURSE: 'En cours',
  };
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const getStatus = (e: { status: StatusKey }) => {
    return status[e.status];
  };
  const normSiret = (s: string | null | undefined): string =>
    (s ?? '').replace(/\s+/g, '').replace(/^FR/i, '');

  const data = useMemo(() => {
    const matchedOrderIds = new Set<number>();
    const exhibRows = response
      .filter((e) => {
        if (filterCollectif === 'individuel') {
          return e.exhibitor_stand_type === filterCollectif;
        }
        if (filterCollectif) {
          return e.collectif === filterCollectif;
        }
        return true;
      })
      .filter((e) => {
        const regex = new RegExp(filterName, 'gi');
        return filterName
          ? regex.test(e.exhibitor_raison_sociale ?? '') ||
              regex.test(e.exhibitor_nom_societe ?? '')
          : true;
      })
      .map((e) => {
        let collectif = '';
        collectif = e.collectif
          ? codifCollectif?.find(
              (ee: any) => ee.topicCode === `collectif:${e.collectif}`
            )?.label ?? ''
          : collectif;
        const eSiret = normSiret(e.exhibitor_siret);
        const matched = eSiret
          ? orders.filter((o) => normSiret(o.siret) === eSiret)
          : [];
        matched.forEach((o: any) => matchedOrderIds.add(o.id));
        return {
          remove: {
            userId: e.user_id,
            exhibId: e.exhibitor_id,
            exhibName: e.exhibitor_raison_sociale,
          },
          new: {
            isNew: isAfter(new Date(e.user_created_at), new Date(2023, 8, 1)),
          },
          status:
            e.status === 'TRANSMIS'
              ? 'Transmis'
              : e.status === 'ENREGISTERED'
              ? 'Enregistré'
              : e.status === 'offre_envoyee'
              ? 'Offre envoyée'
              : 'En cours',
          active: {
            value: !!e.user_email_verified_at,
            userId: e.user_id,
            exhibId: e.exhibitor_id,
          },
          visibility: {
            value: !!e.exhibitor_visibility,
            exhibId: e.exhibitor_id,
          },
          locked: {
            value: !!e.locked,
            exhibId: e.exhibitor_id,
          },
          id: e.exhibitor_id,
          userEmail: e.user_email,
          number: `ID ${String(e.exhibitor_id).padStart(4, '0')}`,
          showroom: {
            value: !!e.exhibitor_showroom,
            exhibId: e.exhibitor_id,
            exhibEventId: e.exhibitor_event_id ?? 0,
          },
          raisonSocial: {
            value: e.exhibitor_raison_sociale,
            exhibId: e.exhibitor_id,
          },
          nomCommercial: e.exhibitor_nom_societe,
          collectif,
          year: { year: e.year, exhibId: e.exhibitor_id },
          numeroHall: {
            stand: e.exhibitor_event_stand,
            hall: e.exhibitor_event_hall,
            exhibId: e.exhibitor_id,
          },
          offre: {
            orders: e.event_orders ?? [],
            offerStatus: e.offer_status ?? '',
            exhibId: e.exhibitor_id,
          },
          edit: { exhibitorId: e.exhibitor_id },
        };
      });

    // Virtual rows for orders not matched to any exhibitor
    // const unmatchedRows = orders
    //   .filter((o: any) => !matchedOrderIds.has(o.id))
    //   .map((o: any) => {
    //     const statusLabels: Record<string, string> = {
    //       offre_envoyee: 'Offre envoyée',
    //       offre_validee: 'Offre validée',
    //       offre_refusee: 'Offre refusée',
    //     };
    //     return {
    //       remove: { userId: 0, exhibId: 0, exhibName: '' },
    //       new: { isNew: false },
    //       status: statusLabels[o.status] ?? o.status,
    //       active: { value: false, userId: 0, exhibId: 0 },
    //       visibility: { value: false, exhibId: 0 },
    //       locked: { value: false, exhibId: 0 },
    //       id: `order-${o.id}`,
    //       userEmail: o.contact_email ?? '',
    //       number: '',
    //       showroom: { value: false, exhibId: 0, exhibEventId: 0 },
    //       raisonSocial: {
    //         value: o.company_name ?? '—',
    //         exhibId: 0,
    //       },
    //       nomCommercial: '',
    //       collectif: '',
    //       year: { year: '', exhibId: 0 },
    //       numeroHall: { stand: '', hall: '', exhibId: 0 },
    //       offre: { orders: [o] },
    //       edit: { exhibitorId: 0 },
    //     };
    //   });

    return [...exhibRows];
  }, [response, codifCollectif, filterCollectif, filterName, orders]);

  const table = useReactTable({
    // data: data.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize),
    data,
    columns,
    state: {
      sorting,
      // pagination,
    },
    // pageCount: Math.ceil(data.length / pageSize) || 1,
    manualPagination: true,
    // onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    // getPaginationRowModel: getPaginationRowModel(),
  });
  const location = useLocation();

  useEffect(() => {
    if (location.state?.reloadKey) {
      setReload((r) => r + 1);
    }
  }, [location.state?.reloadKey]);
  const navigate = useNavigate();
  const closeDrawer = (): void => {
    setReload((prev) => prev + 1);
    navigate(
      pathname.includes('events')
        ? routes.admEventList.path + `/${eventId ?? ''}`
        : ''
    );
  };

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
      {/* Page header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#111' }}>
            {pathname.includes('events') && eventName
              ? `Exhibitors of ${eventName}${eventYear ? ` ${eventYear}` : ''}`
              : t('event:list_of_exhibitors')}
          </Typography>
          {!loading && (
            <Typography sx={{ fontSize: '13px', color: '#888', mt: 0.5 }}>
              {t('event:number_exhibitors')}: {response.length}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          {eventId && (
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                navigate(routes.home2.path + `events/${eventId}/exhibitor-list`)
              }
              sx={{
                height: 40,
                fontSize: 13,
                borderColor: '#ddd',
                color: '#333',
                borderRadius: '8px',
                textTransform: 'none',
              }}
            >
              {t('event:add_exhibitors')}
            </Button>
          )}
          <TextField
            size="small"
            placeholder={t('event:find_an_exhibitors')}
            onChange={(e) => setFilterName(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: '#999' }} />
                </InputAdornment>
              ),
              sx: { borderRadius: '8px', fontSize: 13, bgcolor: '#fff' },
            }}
            sx={{ width: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select
              value={selectedAction}
              // @ts-expect-error
              onChange={handleActionChange}
              displayEmpty
              renderValue={() => (
                <Box
                  component="span"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <IosShareIcon sx={{ fontSize: 16 }} />
                  <span style={{ fontSize: 13 }}>Action</span>
                </Box>
              )}
              sx={{
                borderRadius: '8px',
                bgcolor: '#fff',
                fontSize: 13,
                height: 40,
              }}
            >
              <MenuItem value="exportSimple">
                <CSVLink
                  style={{ textDecoration: 'none', color: 'inherit' }}
                  filename={`export${
                    filterCollectif ? `-${filterCollectif}` : ''
                  }-${format(new Date(), 'dd-MM-yyyy-HH-mm-ss')}.csv`}
                  separator=";"
                  enclosingCharacter={'"'}
                  data={(() => {
                    // At least 3 columns (legacy), more when an event allows more choices
                    const maxThematiques = Math.max(
                      3,
                      ...response.map(
                        (r: any) =>
                          r.exhibitor_extra_fields?.filter(
                            (f: any) => f.field_name === 'thematiques'
                          ).length ?? 0
                      )
                    );
                    const header = [
                      'Status',
                      'ID',
                      'Contact pour ce dossier',
                      'Raison social',
                      'Nom commercial',
                      'Stand',
                      'Hall',
                      'Participation',
                      'Showroom',
                      'Product ID',
                      'Showroom product',
                      'Updated Showroom Product',
                      ...Array.from(
                        { length: maxThematiques },
                        (_, i) => `Thématique${i + 1}`
                      ),
                      'Thématique',
                    ];
                    const rows = data.map((e) => {
                      const raw = response.find(
                        (r: any) => r.exhibitor_id === e.id
                      );
                      const thematiques =
                        raw?.exhibitor_extra_fields
                          ?.filter((f: any) => f.field_name === 'thematiques')
                          .map((f: any) =>
                            String(f.field_value).replace(/"/g, '""')
                          ) ?? [];
                      const showroomProducts =
                        raw?.exhibitor_events_showroom_products ?? [];
                      const showroomProductID =
                        showroomProducts?.[0]?.product?.id ?? '';
                      const showroomName = [
                        showroomProducts?.[0]?.product?.name?.replace(
                          /"/g,
                          '""'
                        ) || '',
                      ];
                      const updatedShowroomProductName =
                        showroomProducts?.[0]?.name?.replace(/"/g, '""') || '';
                      const thematiqueValues = Array.from(
                        { length: maxThematiques },
                        (_, i) => thematiques[i] || ''
                      );
                      const defaultThematique = raw?.thematiques
                        ? raw.thematiques
                        : '';
                      return [
                        e.status.replace(/"/g, '""'),
                        e.id,
                        e.userEmail,
                        e.raisonSocial?.value ?? '',
                        e.nomCommercial?.replace(/"/g, '""') ?? '',
                        e.numeroHall?.stand ?? '',
                        e.numeroHall?.hall ?? '',
                        e.collectif ? e.collectif : 'Individual',
                        e.showroom?.value ? 'Yes' : 'No',
                        showroomProductID,
                        showroomName,
                        updatedShowroomProductName,
                        ...thematiqueValues,
                        defaultThematique,
                      ];
                    });
                    return [header, ...rows];
                  })()}
                >
                  {t('event:simple_extraction')}
                </CSVLink>
              </MenuItem>
              {user?.role !== 'MODERATOR' && (
                <>
                  {csvLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={18} />
                    </MenuItem>
                  ) : (
                    <MenuItem
                      value="uploadCSV"
                      onClick={() => {
                        const fileInput = document.createElement('input');
                        fileInput.type = 'file';
                        fileInput.accept = '.csv';
                        fileInput.onchange = async ({ target }: any) => {
                          setCsvLoading(true);
                          setCsvSuccess(false);
                          setCsvError('');
                          if (!target.files) return;
                          const file = target.files[0];
                          const formdata = new FormData();
                          formdata.append('file', file);
                          formdata.append('eventId', eventId ?? '');
                          try {
                            await exhibitorRepository.uploadCSV(formdata);
                            setCsvSuccess(true);
                          } catch (err: any) {
                            setCsvError(`${err.message}`);
                          } finally {
                            setCsvLoading(false);
                          }
                        };
                        fileInput.click();
                      }}
                    >
                      {t('event:upload_csv')}
                    </MenuItem>
                  )}
                </>
              )}
              <MenuItem value="exportExhibitors" onClick={downloadCSV}>
                {isLoading ? t('event:loading') : t('event:prepare_csv')}
              </MenuItem>
              {isDataReady && (
                <MenuItem value="exportExhibitorsDownload">
                  <CSVLink
                    style={{ textDecoration: 'none', color: 'inherit' }}
                    filename={`export-exhibitors-${format(
                      new Date(),
                      'dd-MM-yyyy-HH-mm-ss'
                    )}.csv`}
                    separator=";"
                    data={csvData}
                  >
                    {t('event:export_exhibitors')}
                  </CSVLink>
                </MenuItem>
              )}
              <MenuItem
                value="downloadPdf"
                onClick={async () => await downloadPdf('')}
              >
                {t('event:download_pdf')}
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Status messages */}
      {csvError && (
        <Box
          sx={{
            p: 1.5,
            bgcolor: '#fee2e2',
            borderRadius: 1,
            mb: 2,
            color: '#dc2626',
            fontSize: 13,
          }}
        >
          {csvError}
        </Box>
      )}
      {csvSuccess && (
        <Box
          sx={{
            p: 1.5,
            bgcolor: '#dcfce7',
            borderRadius: 1,
            mb: 2,
            color: '#16a34a',
            fontSize: 13,
          }}
        >
          CSV importé avec succès
        </Box>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} sx={{ color: '#000' }} />
        </Box>
      )}

      {/* Table */}
      {!loading && (
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '10px',
            border: '1px solid #eee',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ overflowX: 'auto', width: '100%' }}>
            <table className={s.Table}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                        style={{
                          cursor: header.column.getCanSort()
                            ? 'pointer'
                            : 'default',
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                        {header.column.getIsSorted() === 'asc'
                          ? ' ↑'
                          : header.column.getIsSorted() === 'desc'
                          ? ' ↓'
                          : null}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className={s.ExhibitorList__row}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        <div className={s.ExhibitorList__row__div}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Box>
      )}

      {/* Right-side Drawer for exhibitor detail */}
      <Drawer
        anchor="right"
        open={pathname.includes('exhibitor')}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            width: pathname.includes('product')
              ? { xs: '100vw', sm: 900 }
              : { xs: '100vw', sm: 900 },
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {!pathname.includes('product') && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2,
              borderBottom: '1px solid #eee',
              position: 'sticky',
              top: 0,
              bgcolor: '#fff',
              zIndex: 1,
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: 16 }}>
              {t('exhibitors:edit')}
            </Typography>
            <IconButton size="small" onClick={closeDrawer}>
              <CloseIcon />
            </IconButton>
          </Box>
        )}
        <Box sx={{ overflow: 'auto', flex: 1 }}>
          <Outlet />
        </Box>
      </Drawer>
    </Box>
  );
}

export default ExhibitorList;
